import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ArrowUpRight, ChevronLeft, ChevronRight, Check, Download,
  FileText, MessageCircle, Phone, Loader2, ChevronUp, Car,
} from "lucide-react";
import FinancingSimulator from "./FinancingSimulator";
import PremiumFooter from "./PremiumFooter";

const API = process.env.REACT_APP_BACKEND_URL;

const formatPrice = (price) => {
  if (!price) return "Consultar";
  const n = parseFloat(String(price).replace(/[,$]/g, ""));
  return `$${n.toLocaleString("es-MX")}`;
};

const pickImage = (v) => {
  if (v.web_image) return v.web_image;
  if (v.gallery_images?.length) {
    const hook = v.gallery_images.find((i) => i.name === "Hook");
    if (hook?.url) return hook.url;
    return v.gallery_images[0]?.url || null;
  }
  return v.vehicle_image || null;
};

/** Simple sticky header with color-shift between transparent (over dark hero band)
 *  and cream (over the light body). */
function HeaderBar({ business }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      className={`fixed top-0 inset-x-0 z-40 transition-colors duration-500 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-[#0B1623]/8" : "bg-transparent"
      }`}
      data-testid="premium-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
        <Link to={`/catalogo/${business.slug || "automotriz"}`} className="flex items-center gap-3">
          {business.logoUrl ? (
            <img src={business.logoUrl} alt="HEBEH" className="h-10 md:h-12 w-auto" />
          ) : (
            <span className={`font-rajdhani font-bold text-xl tracking-[0.15em] uppercase transition-colors ${scrolled ? "text-[#0B1623]" : "text-white"}`}>HEBEH</span>
          )}
          <span className={`hidden md:inline font-rajdhani text-xs tracking-[0.4em] uppercase transition-colors ${scrolled ? "text-[#0B1623]/60" : "text-white/60"}`}>
            Automotriz
          </span>
        </Link>
        <Link
          to={`/catalogo/${business.slug || "automotriz"}`}
          className={`inline-flex items-center gap-2 px-5 py-2 rounded-full border font-rajdhani font-medium text-sm tracking-[0.2em] uppercase transition-colors ${
            scrolled
              ? "border-[#0B1623]/25 text-[#0B1623] hover:bg-[#0B1623] hover:text-white"
              : "border-white/25 text-white hover:bg-white hover:text-[#0B1623]"
          }`}
          data-testid="header-back-catalog"
        >
          <ArrowLeft className="w-4 h-4" /> Catálogo
        </Link>
      </div>
    </header>
  );
}

function FloatingActions({ business }) {
  const [showTop, setShowTop] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const wa = business?.whatsapp || "5539835137";
  return (
    <div className="fixed right-5 bottom-24 md:right-8 md:bottom-28 z-50 flex flex-col gap-3 items-end">
      {showTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="w-12 h-12 rounded-full bg-white/95 text-[#0B1623] shadow-[0_8px_24px_rgba(11,22,35,0.15)] flex items-center justify-center hover:scale-105 transition-transform"
          aria-label="Ir arriba" data-testid="float-scroll-top"
        >
          <ChevronUp className="w-5 h-5" />
        </button>
      )}
      <a
        href={`https://api.whatsapp.com/send?phone=${wa}`}
        target="_blank" rel="noopener noreferrer"
        className="w-14 h-14 rounded-full bg-[#25D366] text-white shadow-[0_10px_30px_rgba(37,211,102,0.35)] flex items-center justify-center hover:scale-105 transition-transform"
        aria-label="WhatsApp" data-testid="float-whatsapp"
      >
        <MessageCircle className="w-6 h-6" />
      </a>
    </div>
  );
}

/** Similar vehicles — filter by price proximity (same tier), never by brand alone.
 *  Keeps only vehicles within ±40% of current price; ties broken by absolute price diff. */
// Genera el path a un vehículo — usa slug SEO-friendly en el subdominio del catálogo,
// y ruta clásica con UUID en cualquier otro dominio (admin).
function vehicleHref(v, businessSlug) {
  if (typeof window === "undefined") return `/catalogo/${businessSlug}/${v.id}`;
  const host = window.location.hostname.toLowerCase();
  const isCatalogSub = host.startsWith("automotriz") || host === "automotriz";
  if (isCatalogSub && v.brand && v.model_name) {
    return `/${slugify(v.brand)}/${slugify(v.model_name)}`;
  }
  return `/catalogo/${businessSlug}/${v.id}`;
}

function SimilarVehicles({ currentVehicle, businessSlug }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/public/vehicles?business_id=automotriz`);
        if (!r.data?.success) return;
        const priceOf = (v) => parseFloat(String(v.price || "0").replace(/[,$]/g, "")) || 0;
        const cp = priceOf(currentVehicle);

        const all = (r.data.vehicles || []).filter((v) => v.id !== currentVehicle.id);

        // Dedupe by brand+model+year keeping the cheapest per group.
        const groups = new Map();
        for (const v of all) {
          const key = `${(v.brand || "").toLowerCase()}|${(v.model_name || "").toLowerCase()}|${(v.year || "")}`;
          const ex = groups.get(key);
          if (!ex || priceOf(v) < priceOf(ex)) groups.set(key, v);
        }
        const uniq = Array.from(groups.values());

        // Filter: only same "tier" (price within ±40% of current). If empty, do NOT fallback —
        // it's better to hide the section entirely than to suggest something in a different tier.
        const withinTier = uniq.filter((v) => {
          const p = priceOf(v);
          if (!p || !cp) return false;
          const ratio = p / cp;
          return ratio >= 0.6 && ratio <= 1.4;
        });

        // Sort by absolute price difference (closer = better match)
        withinTier.sort((a, b) => Math.abs(priceOf(a) - cp) - Math.abs(priceOf(b) - cp));

        setItems(withinTier.slice(0, 6));
      } catch { /* silent */ }
    })();
  }, [currentVehicle]);

  if (!items.length) return null;

  return (
    <section className="bg-white py-16 md:py-24" data-testid="similar-vehicles">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-10 md:mb-14">
          <span className="font-rajdhani font-medium text-[#3B82F6] text-xs md:text-sm tracking-[0.4em] uppercase">
            Vehículos similares
          </span>
          <h2 className="font-rajdhani font-semibold text-[#0B1623] tracking-tight leading-[0.95] text-3xl sm:text-4xl md:text-5xl mt-4">
            También podrían interesarte.
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {items.slice(0, 3).map((v, i) => {
            const img = pickImage(v);
            return (
              <motion.div
                key={v.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                data-testid={`similar-card-${v.id}`}
              >
                <Link
                  to={vehicleHref(v, businessSlug)}
                  className="group relative block rounded-2xl overflow-hidden bg-white border border-[#0B1623]/8 hover:border-[#0B1623]/25 shadow-[0_2px_8px_rgba(11,22,35,0.04)] hover:shadow-[0_20px_50px_rgba(11,22,35,0.10)] transition-all duration-500"
                >
                  <div className="relative aspect-[4/3] bg-[#F5F5F5] overflow-hidden">
                    {img ? (
                      <img src={img} alt={v.model_name} className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.06]" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Car className="w-14 h-14 text-[#0B1623]/20" /></div>
                    )}
                  </div>
                  <div className="p-5 md:p-6">
                    <p className="font-rajdhani font-medium text-[#3B82F6] text-xs tracking-[0.3em] uppercase mb-2">
                      {v.brand || "Vehículo"}
                    </p>
                    <h3 className="font-rajdhani font-semibold text-[#0B1623] text-xl md:text-2xl leading-tight tracking-tight">
                      {v.model_name}
                    </h3>
                    <p className="font-manrope text-[#0B1623]/55 text-sm mt-1">{v.year || ""}</p>
                    <div className="mt-5 flex items-end justify-between pt-4 border-t border-[#0B1623]/8">
                      <div>
                        <p className="font-manrope text-[10px] uppercase tracking-widest text-[#0B1623]/45">Precio desde</p>
                        <p className="font-rajdhani font-semibold text-xl md:text-2xl text-[#0B1623] mt-0.5">{formatPrice(v.price)}</p>
                      </div>
                      <span className="font-rajdhani font-medium text-sm text-[#0B1623]/70 group-hover:text-[#3B82F6] transition-colors inline-flex items-center gap-1 uppercase tracking-wider">
                        Ver más <ArrowUpRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// Slugify helper (frontend, mismo criterio que backend `_slugify`).
function slugify(text) {
  return String(text || "")
    .toLowerCase()
    .normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// Inyecta o actualiza meta tags en el <head> para SEO por página.
// Cubre: title, description, canonical, Open Graph, Twitter Card, JSON-LD (schema.org).
function useSEO({ title, description, url, image, price, vehicle }) {
  useEffect(() => {
    if (!title) return;
    document.title = title;
    const set = (attr, key, value) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value || "");
    };
    if (description) set("name", "description", description);
    set("property", "og:title", title);
    if (description) set("property", "og:description", description);
    if (url) set("property", "og:url", url);
    if (image) set("property", "og:image", image);
    set("property", "og:type", "product");
    set("property", "og:site_name", "HEBEH Automotriz");
    set("property", "og:locale", "es_MX");
    set("name", "twitter:card", "summary_large_image");
    if (title) set("name", "twitter:title", title);
    if (description) set("name", "twitter:description", description);
    if (image) set("name", "twitter:image", image);
    // Canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    if (url) canonical.setAttribute("href", url);
    // JSON-LD Schema.org Product/Car — lo que Google/ChatGPT/Perplexity leen
    // para poner a HEBEH como resultado de búsqueda o recomendación IA.
    let ld = document.getElementById("hebeh-jsonld");
    if (!ld) {
      ld = document.createElement("script");
      ld.setAttribute("type", "application/ld+json");
      ld.id = "hebeh-jsonld";
      document.head.appendChild(ld);
    }
    if (vehicle) {
      const priceNum = Number(String(price || "").replace(/[^0-9.]/g, "")) || undefined;
      const data = {
        "@context": "https://schema.org",
        "@type": "Car",
        name: title,
        description,
        brand: { "@type": "Brand", name: vehicle.brand || "" },
        model: vehicle.model_name || "",
        vehicleModelDate: vehicle.year || undefined,
        image: image ? [image] : undefined,
        url,
        offers: priceNum ? {
          "@type": "Offer",
          priceCurrency: "MXN",
          price: priceNum,
          availability: "https://schema.org/InStock",
          url,
          seller: {
            "@type": "AutoDealer",
            name: "HEBEH Automotriz",
            url: "https://automotriz.hebeh.com.mx",
            areaServed: "MX",
          },
        } : undefined,
      };
      ld.textContent = JSON.stringify(data, null, 2);
    }
  }, [title, description, url, image, price, vehicle]);
}

export default function VehicleDetailPremium() {
  const { vehicleId, businessSlug, brandSlug, modelSlug } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [interestRate, setInterestRate] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [business, setBusiness] = useState({ slug: businessSlug || "automotriz", whatsapp: "5539835137", phone: "55 1318 1745", accentColor: "#3B82F6" });
  const [versions, setVersions] = useState([]);
  const [activeVersionId, setActiveVersionId] = useState(null);
  const [galleryIdx, setGalleryIdx] = useState(0);
  const [galleryExtra, setGalleryExtra] = useState(null); // { gallery_images, web_gallery_images, vehicle_image }

  useEffect(() => {
    (async () => {
      setLoading(true);
      setGalleryExtra(null);
      try {
        // Endpoint: si vino por /:brand/:model → resolver por slugs; si vino por UUID → directo.
        const endpoint = vehicleId
          ? `${API}/api/public/vehicles/${vehicleId}`
          : `${API}/api/public/vehicles/by-slugs/${brandSlug}/${modelSlug}`;
        const r = await axios.get(endpoint);
        if (r.data?.success) {
          setVehicle(r.data.vehicle);
          setInterestRate(r.data.interest_rate || 20);
          if (r.data.business) setBusiness((p) => ({ ...p, ...r.data.business }));
          const vs = r.data.versions || [];
          setVersions(vs);
          const cur = r.data.vehicle?.id;
          setActiveVersionId(vs.find((x) => x.id === cur) ? cur : (vs[0]?.id || cur));
          // Actualizar la URL para que sea el slug limpio (SEO), sin recargar la página.
          const v = r.data.vehicle;
          const host = window.location.hostname.toLowerCase();
          const isAutosSub = host.startsWith("automotriz") || host === "automotriz";
          if (isAutosSub && v?.brand && v?.model_name) {
            const cleanPath = `/${slugify(v.brand)}/${slugify(v.model_name)}`;
            if (window.location.pathname !== cleanPath) {
              window.history.replaceState(null, "", cleanPath);
            }
          }
          // Cargar galería en lazy (después del primer render).
          const idForGallery = r.data.vehicle?.id;
          if (idForGallery) {
            axios.get(`${API}/api/public/vehicles/${idForGallery}/gallery`)
              .then((gr) => { if (gr.data?.success) setGalleryExtra(gr.data); })
              .catch(() => {});
          }
        } else setError("No encontrado");
      } catch { setError("Error al cargar"); }
      finally { setLoading(false); }
    })();
  }, [vehicleId, brandSlug, modelSlug]);

  const gallery = useMemo(() => {
    if (!vehicle) return [];
    const arr = [];
    // Only include Web images (Web 1/2/3 etc.) — exclude Hook, Lifestyle, Trabajo, Stories
    // and any other image that has text overlays.
    if (vehicle.web_image) arr.push({ url: vehicle.web_image, name: "Web" });
    const webGallery = galleryExtra?.web_gallery_images || [];
    const rawGallery = galleryExtra?.gallery_images || [];
    const rawVehicleImage = galleryExtra?.vehicle_image || "";
    webGallery.forEach((g) => { if (g.url) arr.push(g); });
    rawGallery.forEach((g) => {
      if (g.url && typeof g.name === "string" && g.name.toLowerCase().startsWith("web")) {
        arr.push(g);
      }
    });
    // Fallback: if there is absolutely nothing "web" but we do have vehicle_image, show it.
    if (!arr.length && rawVehicleImage) arr.push({ url: rawVehicleImage, name: "Principal" });
    // Dedupe by URL
    const seen = new Set();
    return arr.filter((g) => { if (seen.has(g.url)) return false; seen.add(g.url); return true; });
  }, [vehicle, galleryExtra]);

  // === SEO: title + description + Open Graph + JSON-LD ===
  // Se calcula ANTES de los early returns para no violar rules-of-hooks.
  const _active = vehicle ? versions.find((v) => v.id === activeVersionId) : null;
  const _activePrice = _active?.price ?? vehicle?.price;
  const _activeVersionName = _active?.version ?? vehicle?.version;
  const _activeVehicleName = vehicle
    ? `${vehicle.brand || ""} ${vehicle.model_name || ""} ${_activeVersionName || ""}`.trim()
    : "";
  const _heroImg = gallery[0]?.url;
  const _seoTitle = vehicle
    ? `${_activeVehicleName} ${vehicle.year || ""} desde ${formatPrice(_activePrice)} | HEBEH Automotriz`.replace(/\s+/g, " ").trim()
    : "";
  const _seoDescription = vehicle
    ? `Compra ${_activeVehicleName} ${vehicle.year || ""} en HEBEH Automotriz — precio desde ${formatPrice(_activePrice)}, financiamiento disponible, agencia oficial en Ciudad de México. Contáctanos por WhatsApp.`
    : "";
  const _seoUrl = (vehicle && typeof window !== "undefined")
    ? `${window.location.origin}/${slugify(vehicle.brand)}/${slugify(vehicle.model_name)}`
    : "";
  const _seoImage = _heroImg && !_heroImg.startsWith("data:") ? _heroImg : "";
  useSEO({
    title: _seoTitle,
    description: _seoDescription,
    url: _seoUrl,
    image: _seoImage,
    price: _activePrice,
    vehicle,
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1623] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-[#3B82F6]" />
      </div>
    );
  }
  if (error || !vehicle) {
    return (
      <div className="min-h-screen bg-[#0B1623] flex items-center justify-center">
        <div className="text-center">
          <Car className="w-16 h-16 mx-auto text-slate-600 mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">{error || "Vehículo no encontrado"}</h2>
          <Link to={`/catalogo/${businessSlug}`} className="inline-flex items-center gap-2 mt-4 text-[#3B82F6]">
            <ArrowLeft className="w-4 h-4" /> Volver al catálogo
          </Link>
        </div>
      </div>
    );
  }

  const active = versions.find((v) => v.id === activeVersionId);
  const activePrice = active?.price ?? vehicle.price;
  const activeSpecs = (active?.specifications?.length) ? active.specifications : (vehicle.specifications || []);
  const activeSpecSheet = active?.spec_sheet_url ?? vehicle.spec_sheet_url;
  const activeVersionName = active?.version ?? vehicle.version;
  const activeVehicleName = `${vehicle.brand} ${vehicle.model_name} ${activeVersionName || ""}`.trim();
  const showVersionTabs = versions.length > 1;
  const heroImg = gallery[0]?.url;

  return (
    <div className="min-h-screen bg-white text-[#0B1623] font-manrope">
      <HeaderBar business={business} />

      {/* HERO — dark cinematic band (very compact) with the vehicle title right-aligned */}
      <section className="relative w-full bg-[#0B1623] pt-16 pb-3 md:pt-20 md:pb-4 overflow-hidden" data-testid="detail-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap items-baseline justify-end gap-x-4 gap-y-1 text-right">
            <h1 className="font-rajdhani font-semibold text-white text-xl sm:text-2xl md:text-3xl tracking-tight leading-none" data-testid="vehicle-title">
              {vehicle.model_name}
            </h1>
            <p className="font-manrope text-white/60 text-xs md:text-sm">
              {activeVersionName} {vehicle.year && `· ${vehicle.year}`}
            </p>
          </div>
        </div>
      </section>

      {/* MAIN — Two columns: gallery + details */}
      <section className="bg-white py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-12">
          {/* Gallery */}
          <div className="lg:col-span-3">
            <div className="relative rounded-2xl overflow-hidden bg-[#F5F5F5] aspect-[4/3]" data-testid="gallery-main">
              {heroImg ? (
                <img src={gallery[galleryIdx]?.url || heroImg} alt={vehicle.model_name} className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center"><Car className="w-20 h-20 text-[#0B1623]/20" /></div>
              )}
              {gallery.length > 1 && (
                <>
                  <button
                    onClick={() => setGalleryIdx((i) => (i - 1 + gallery.length) % gallery.length)}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 text-[#0B1623] hover:bg-white shadow flex items-center justify-center"
                    aria-label="Anterior" data-testid="gallery-prev"
                  ><ChevronLeft className="w-5 h-5" /></button>
                  <button
                    onClick={() => setGalleryIdx((i) => (i + 1) % gallery.length)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 text-[#0B1623] hover:bg-white shadow flex items-center justify-center"
                    aria-label="Siguiente" data-testid="gallery-next"
                  ><ChevronRight className="w-5 h-5" /></button>
                </>
              )}
            </div>

            {gallery.length > 1 && (
              <div className="mt-4 grid grid-cols-5 gap-3" data-testid="gallery-thumbs">
                {gallery.slice(0, 10).map((g, k) => (
                  <button
                    key={k}
                    onClick={() => setGalleryIdx(k)}
                    className={`rounded-lg overflow-hidden aspect-[4/3] border-2 transition-colors ${k === galleryIdx ? "border-[#3B82F6]" : "border-transparent hover:border-[#0B1623]/20"}`}
                    aria-label={`Imagen ${k + 1}`}
                  >
                    <img src={g.url} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl bg-white border border-[#0B1623]/8 p-6 md:p-8 shadow-[0_10px_40px_rgba(11,22,35,0.06)]" data-testid="vehicle-details">
              {showVersionTabs && (
                <div className="mb-5" data-testid="version-tabs">
                  <p className="font-rajdhani text-[10px] tracking-[0.3em] uppercase text-[#0B1623]/50 mb-2">Elige tu versión</p>
                  <div className="flex flex-wrap gap-2">
                    {versions.map((ver) => {
                      const isActive = ver.id === activeVersionId;
                      const pn = parseFloat(String(ver.price || "0").replace(/[,$]/g, "")) || 0;
                      return (
                        <button
                          key={ver.id}
                          onClick={() => setActiveVersionId(ver.id)}
                          className={`px-3 py-2 rounded-lg text-xs font-rajdhani font-semibold uppercase tracking-wide border transition-all ${
                            isActive
                              ? "bg-[#3B82F6]/10 border-[#3B82F6] text-[#3B82F6]"
                              : "bg-white border-[#0B1623]/12 text-[#0B1623]/70 hover:border-[#0B1623]/25"
                          }`}
                          data-testid={`version-tab-${ver.id}`}
                        >
                          <div className="text-left">
                            <div>{ver.version || "Base"}</div>
                            {pn > 0 && <div className="text-[10px] opacity-70">${pn.toLocaleString("es-MX")}</div>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="pb-5 border-b border-[#0B1623]/8">
                <p className="font-manrope text-[10px] uppercase tracking-[0.3em] text-[#0B1623]/50">Precio</p>
                <p className="font-rajdhani font-semibold text-4xl md:text-5xl text-[#0B1623] mt-1" data-testid="vehicle-price">
                  {formatPrice(activePrice)}
                </p>
                <p className="mt-2 text-sm text-green-600 font-manrope">✓ Financiamiento disponible</p>
              </div>

              {activeSpecs.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-rajdhani font-semibold text-[#0B1623] text-lg mb-3">Especificaciones</h4>
                  <ul className="space-y-2">
                    {activeSpecs.map((s, k) => (
                      <li key={k} className="flex items-start gap-2 text-[#0B1623]/80 text-sm font-manrope" data-testid={`spec-${k}`}>
                        <Check className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#3B82F6]" />
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {activeSpecSheet && (
                <div className="mt-6">
                  <a
                    href={activeSpecSheet}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0B1623]/15 text-[#0B1623]/80 hover:border-[#3B82F6] hover:text-[#3B82F6] transition-colors text-sm font-manrope"
                    data-testid="download-spec-sheet"
                  >
                    <FileText className="w-4 h-4" /> Descargar ficha técnica <Download className="w-4 h-4" />
                  </a>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 mt-6 pt-6 border-t border-[#0B1623]/8">
                <a
                  href={`https://api.whatsapp.com/send?phone=${business.whatsapp || "5539835137"}&text=${encodeURIComponent(`Hola, me interesa el ${activeVehicleName}`)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 py-3 rounded-full bg-[#25D366] text-white font-rajdhani font-semibold text-sm uppercase tracking-[0.15em] hover:bg-[#20BD5C] transition-colors"
                  data-testid="cta-whatsapp"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
                <a
                  href={`tel:${business.whatsapp || "5539835137"}`}
                  className="inline-flex items-center justify-center gap-2 py-3 rounded-full border border-[#0B1623] text-[#0B1623] font-rajdhani font-semibold text-sm uppercase tracking-[0.15em] hover:bg-[#0B1623] hover:text-white transition-colors"
                  data-testid="cta-call"
                >
                  <Phone className="w-4 h-4" /> Llamar
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Financing simulator with the active price prefilled and locked */}
      <FinancingSimulator
        interestRate={interestRate}
        whatsapp={business.whatsapp}
        lockedPrice={activePrice}
        vehicleName={activeVehicleName}
      />

      {/* Similar vehicles */}
      <SimilarVehicles currentVehicle={vehicle} businessSlug={businessSlug} />

      <PremiumFooter business={business} />
      <FloatingActions business={business} />
    </div>
  );
}
